/**
 * ReconnectBadge — Surfaces the useChatStream reconnect state as a visible Badge.
 *
 * State mapping:
 *   'idle'         → returns null (no badge)
 *   'reconnecting' → amber Badge: "Reconnecting..."
 *   'failed'       → red/destructive Badge: "Connection lost"
 *
 * The 'failed' badge intentionally stays visible (not auto-dismissed) so
 * users understand the retry loop has exhausted its attempts. The ChatErrorCard
 * rendered inside the chat thread then carries the actionable "Retry" CTA.
 *
 * Props:
 *   reconnectState — sourced from useChatStream().reconnectState
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §2.5 + §3.6 Track 6 + §5 Wave 3B
 */

import { Loader2, WifiOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ReconnectState } from '@/lib/chat/use-chat-stream';

export interface ReconnectBadgeProps {
  /** Sourced directly from useChatStream().reconnectState */
  reconnectState: ReconnectState;
  /** Optional extra class names applied to the Badge wrapper. */
  className?: string;
}

/**
 * Compact inline badge shown adjacent to the ArgusEye / streaming area
 * when the client-side reconnect machine is active.
 *
 * Returns null when reconnectState === 'idle' so the component carries
 * no DOM footprint during normal operation.
 */
export function ReconnectBadge({ reconnectState, className }: ReconnectBadgeProps) {
  if (reconnectState === 'idle') {
    return null;
  }

  if (reconnectState === 'reconnecting') {
    return (
      <Badge
        variant="warning"
        className={cn(
          'gap-1 text-[11px] font-medium select-none',
          className,
        )}
        aria-live="polite"
        aria-label="Reconnecting to Ask Argus"
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        Reconnecting...
      </Badge>
    );
  }

  // reconnectState === 'failed'
  return (
    <Badge
      variant="destructive"
      className={cn(
        'gap-1 text-[11px] font-medium select-none',
        className,
      )}
      aria-live="assertive"
      aria-label="Connection to Ask Argus lost"
    >
      <WifiOff className="h-3 w-3" aria-hidden="true" />
      Connection lost
    </Badge>
  );
}
